<script setup lang="ts">
import { computed } from 'vue';
import Icon from '@/components/atoms/IconBase.vue';
import Tooltip from '@/components/atoms/Tooltip.vue';

defineOptions({
  name: 'IconButton',
});

interface Props {
  text: string;
  tooltipSize?: number;
  direction?: string;
  iconName: string;
  size?: 'xlarge' | 'large' | 'medium' | 'small';
  color?: 'white' | 'black' | 'gray' | 'yellow' | 'red';
  ariaLabel: string;
  isActive?: boolean;
  href?: string;
  isDisabled?: boolean;
  onClick?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  tooltipSize: undefined,
  direction: 'up',
  size: 'large',
  color: 'black',
  isActive: false,
  href: undefined,
  isDisabled: false,
  onClick: undefined,
});

const tag = computed(() => {
  return props.href ? 'router-link' : 'button';
});
</script>

<template>
  <Tooltip
    class="IconButton"
    :class="[
      `IconButton--${size}`,
      `IconButton--${color}`,
      {
        'IconButton--active': isActive,
        'IconButton--disabled': isDisabled,
      },
    ]"
    :text="text"
    :direction="direction"
    :size="tooltipSize"
  >
    <component
      :is="tag"
      class="IconButton__button"
      :aria-label="ariaLabel"
      :to="href"
      @click="onClick"
    >
      <Icon class="IconButton__icon" :icon-name="iconName" />
    </component>
  </Tooltip>
</template>

<style lang="scss" scoped>
@use '@/assets/scss/_forward.scss' as *;

$size-styles: (
  'xlarge': (
    'width': $service-unit * 8,
    'height': $service-unit * 8,
  ),
  'large': (
    'width': $service-unit * 6,
    'height': $service-unit * 6,
  ),
  'medium': (
    'width': $service-unit * 5,
    'height': $service-unit * 5,
  ),
  'small': (
    'width': $service-unit * 4,
    'height': $service-unit * 4,
  ),
);

$color-styles: (
  'white': (
    'color': $color-white-100,
    'background-color': $color-white-100,
  ),
  'black': (
    'color': $color-gray-80,
    'background-color': $color-gray-20,
  ),
  'gray': (
    'color': $color-gray-60,
    'background-color': $color-gray-20,
  ),
  'yellow': (
    'color': $color-yellow-80,
    'background-color': $color-gray-20,
  ),
  'red': (
    'color': $color-red-70,
    'background-color': $color-gray-20,
  ),
);

.IconButton {
  @each $size, $style in $size-styles {
    &--#{$size} {
      width: map-get($style, 'width');
      height: map-get($style, 'height');
    }
  }

  &__button {
    position: relative;
    display: block;
    cursor: pointer;
    background-color: transparent;

    &::before {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      display: none;
      content: '';
      border-radius: 50%;
    }

    &:hover {
      &::before {
        display: block;
        word-wrap: none;
      }
    }
  }

  @each $size, $style in $size-styles {
    &--#{$size} &__button {
      width: map-get($style, 'width');
      height: map-get($style, 'height');

      &::before {
        width: map-get($style, 'width');
        height: map-get($style, 'height');
      }
    }
  }

  @each $color, $style in $color-styles {
    &--#{$color} &__button {
      &::before {
        background-color: map-get($style, 'background-color');

        @if $color == 'white' {
          opacity: 0.2;
        }
      }
    }
  }

  &--active &__button {
    &::before {
      display: block;
      border-radius: 0;
    }
  }

  & &__icon {
    position: relative;
    z-index: 1;
  }

  &--disabled &__button,
  &--disabled &__icon {
    pointer-events: none;

    :deep(.Icon) {
      color: $color-gray-50;
    }
  }

  @each $size, $style in $size-styles {
    &--#{$size} &__icon {
      width: map-get($style, 'width');
      height: map-get($style, 'height');
    }
  }

  @each $color, $style in $color-styles {
    &--#{$color} &__icon {
      color: map-get($style, 'color');
    }
  }
}
</style>
